import Navbar from '@/components/landing/Navbar'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import {
  Store,
  Target,
  Lightbulb,
  Users,
  ShieldCheck,
  ArrowRight,
} from 'lucide-react'
import { FinalCta } from '@/components/landing'

export default function AboutPage() {
  return (
    <div>
      <Navbar />

      {/* HERO */}
      <section className='relative overflow-hidden border-b'>
        <div className='absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_-10%,hsl(var(--primary)/.14),transparent_60%)]' />

        <div className='relative mx-auto max-w-7xl px-4 py-20 md:py-24 text-center'>
          <Badge variant='secondary'>À propos</Badge>

          <h1 className='mt-4 text-4xl md:text-5xl font-extrabold tracking-tight'>
            Pourquoi <span className='text-primary'>MOGESAPP</span> existe
          </h1>

          <p className='mt-4 max-w-2xl mx-auto text-muted-foreground'>
            MOGESAPP est né d’un problème simple : beaucoup de commerces gèrent
            encore leurs ventes, leur stock et leurs paiements avec des cahiers
            ou des outils dispersés.
          </p>
        </div>
      </section>

      {/* PROBLEME */}
      <section className='mx-auto max-w-7xl px-4 py-16'>
        <div className='grid gap-8 md:grid-cols-2 items-start'>
          <div>
            <div className='flex items-center gap-2 text-primary font-semibold'>
              <Target className='h-4 w-4' />
              Le problème
            </div>

            <h2 className='mt-2 text-3xl font-bold'>
              Beaucoup de commerces manquent de visibilité
            </h2>

            <p className='mt-4 text-muted-foreground leading-7'>
              Dans de nombreux commerces, il devient vite difficile de savoir
              clairement :
            </p>

            <ul className='mt-4 space-y-2 text-muted-foreground'>
              <li>• combien de produits restent réellement en stock</li>
              <li>• quels clients doivent encore payer</li>
              <li>• quelles ventes ont été faites dans la journée</li>
              <li>• où part l’argent de l’activité</li>
            </ul>

            <p className='mt-4 text-muted-foreground'>
              Quand les informations sont dispersées, la gestion devient plus
              compliquée et les erreurs se multiplient.
            </p>
          </div>

          <Card className='p-6'>
            <div className='flex items-center gap-2 font-semibold'>
              <Lightbulb className='h-4 w-4 text-primary' />
              La solution
            </div>

            <p className='mt-3 text-sm text-muted-foreground'>
              MOGESAPP centralise ventes, stock, clients et paiements dans une
              seule application simple à utiliser.
            </p>

            <p className='mt-3 text-sm text-muted-foreground'>
              L’objectif est de permettre aux commerçants de mieux comprendre
              leur activité et prendre des décisions plus facilement.
            </p>
          </Card>
        </div>
      </section>

      {/* PHILOSOPHIE */}
      <section className='border-t bg-muted/20'>
        <div className='mx-auto max-w-7xl px-4 py-16'>
          <div className='text-center max-w-2xl mx-auto'>
            <h2 className='text-3xl font-bold'>La philosophie du produit</h2>
            <p className='mt-3 text-muted-foreground'>
              MOGESAPP est conçu avec quelques principes simples.
            </p>
          </div>

          <div className='mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4'>
            {[
              {
                icon: Lightbulb,
                title: 'Simplicité',
                desc: 'Une application facile à comprendre et à utiliser.',
              },
              {
                icon: Store,
                title: 'Pensé pour les commerces',
                desc: 'Conçu pour la réalité des boutiques et magasins.',
              },
              {
                icon: Users,
                title: 'Collaboration',
                desc: 'Permet de travailler à plusieurs dans le même système.',
              },
              {
                icon: ShieldCheck,
                title: 'Fiabilité',
                desc: 'Des données claires pour prendre de meilleures décisions.',
              },
            ].map((item) => {
              const Icon = item.icon
              return (
                <Card key={item.title} className='p-6'>
                  <div className='flex items-center gap-2 font-semibold'>
                    <Icon className='h-4 w-4 text-primary' />
                    {item.title}
                  </div>

                  <p className='mt-3 text-sm text-muted-foreground'>
                    {item.desc}
                  </p>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* POUR QUI */}
      <section className='mx-auto max-w-7xl px-4 py-16'>
        <div className='max-w-2xl'>
          <h2 className='text-3xl font-bold'>Pour qui est fait MOGESAPP</h2>

          <p className='mt-4 text-muted-foreground'>
            MOGESAPP est conçu pour les activités qui vendent des produits et
            ont besoin de suivre leur stock et leurs paiements.
          </p>

          <ul className='mt-6 space-y-2 text-muted-foreground'>
            <li>• boutiques et magasins</li>
            <li>• supérettes</li>
            <li>• commerces de détail</li>
            <li>• activités avec gestion de stock</li>
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className='border-t bg-muted/30'>
        <div className='mx-auto max-w-7xl px-4 py-12 text-center space-y-4'>
          <h3 className='text-2xl font-bold'>
            Essayez MOGESAPP et voyez la différence
          </h3>

          <p className='text-muted-foreground'>
            Créez votre compte gratuitement et commencez à organiser votre
            activité.
          </p>

          <div className='flex justify-center gap-3'>
            <Link to='/register?redirect=/dashboard'>
              <Button size='lg'>
                Essai gratuit
                <ArrowRight className='ml-2 h-4 w-4' />
              </Button>
            </Link>

            <Link to='/documentation'>
              <Button variant='outline' size='lg'>
                Lire la documentation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <FinalCta />
    </div>
  )
}
